import { plural } from '@cian/utils';

import { type PropsWithChildren, type ReactNode, useCallback } from 'react';

import { type TModifier } from '@/packages/Filters';
import { MaskedInput } from '@/packages/Filters/shared/components/MaskedInput';
import { RadioButtonGroup } from '@/packages/Filters/shared/components/RadioButtonGroup';
import { getTravelTypeByOfferType } from '@/packages/Filters/shared/components/advancedFilters/UndergroundTime/utils/getTravelTypeByOfferType';
import { InlineFilter } from '@/packages/Filters/shared/components/advancedFilters/common/InlineFilter';
import { InlineFilterGroup } from '@/packages/Filters/shared/components/advancedFilters/common/InlineFilterGroup';
import { DEFAULT_UNDERGROUND_TRAVEL_TIME } from '@/packages/Filters/shared/constants/underground';
import { type NonEmptyArray, type TJsonQuery, getOfferTypeFromJsonQuery } from '@/packages/JsonQuery';
import { EMetroTravelType } from '@/packages/api-models/common/json_query';

import { UndergroundMapModalHeader } from '../../components/UndergroundMapModalHeader';

interface ITravelDataChange {
  travelTime: number | null;
  travelType: EMetroTravelType | null;
}
interface IUndergroundTimeOption {
  value: EMetroTravelType | null;
  label: string;
}

const UNDERGROUND_TRAVEL_OPTIONS: IUndergroundTimeOption[] = [
  {
    value: EMetroTravelType.MapFoot,
    label: 'Пешком',
  },
  {
    value: EMetroTravelType.MapTransport,
    label: 'Транспортом',
  },
];

type UndergroundMapModalHeaderContainerProps = {
  jsonQuery: TJsonQuery;
  onChange: (...modifiers: NonEmptyArray<TModifier>) => void;
};

export function UndergroundMapModalHeaderContainer({
  children,
  onChange,
  jsonQuery,
}: PropsWithChildren<UndergroundMapModalHeaderContainerProps>): ReactNode {
  const offerType = getOfferTypeFromJsonQuery(jsonQuery);
  const travelType = jsonQuery.only_foot?.value ?? null;
  const travelTime = jsonQuery.foot_min?.value.lte ?? null;

  const handleChangeTravelTime = useCallback(
    (time: number | null) => {
      onChange({ action: 'setUndergroundTravelTime', arguments: [time] });
    },
    [onChange],
  );

  const handleChangeTravelType = useCallback(
    (type: EMetroTravelType | null) => {
      onChange({ action: 'setUndergroundTravelType', arguments: [type] });
    },
    [onChange],
  );

  const handleChangeTravelData = useCallback(
    (value: ITravelDataChange) => {
      onChange(
        { action: 'setUndergroundTravelType', arguments: [value.travelType] },
        { action: 'setUndergroundTravelTime', arguments: [value.travelTime] },
      );
    },
    [onChange],
  );

  const handleTravelTimeChange = useCallback(
    (time: string) => {
      const nextTime = time ? Number(time) : null;

      if (nextTime && !travelType) {
        const nextTravelType = getTravelTypeByOfferType(offerType);

        handleChangeTravelData({ travelTime: nextTime, travelType: nextTravelType });
      } else if (!nextTime && travelType) {
        handleChangeTravelData({ travelTime: nextTime, travelType: null });
      } else {
        handleChangeTravelTime(nextTime);
      }
    },
    [offerType, handleChangeTravelData, handleChangeTravelTime, travelType],
  );

  const handleTravelTypeChange = useCallback(
    (type: EMetroTravelType | null) => {
      if (!travelTime && type) {
        handleChangeTravelData({ travelTime: DEFAULT_UNDERGROUND_TRAVEL_TIME, travelType: type });
      } else {
        handleChangeTravelType(type);
      }
    },
    [travelTime, handleChangeTravelData, handleChangeTravelType],
  );

  return (
    <UndergroundMapModalHeader>
      <InlineFilterGroup>
        <InlineFilter
          postfix={(travelTime && plural(travelTime, ['минуты', 'минут', 'минут'])) || 'минут'}
          prefix="До метро не более"
        >
          <MaskedInput
            inline
            mask={Number}
            max={300}
            min={1}
            placeholder="10"
            value={travelTime === null ? '' : String(travelTime)}
            width={50}
            onComplete={handleTravelTimeChange}
          />
        </InlineFilter>
        <InlineFilter>
          <RadioButtonGroup
            options={UNDERGROUND_TRAVEL_OPTIONS}
            spliced={false}
            value={travelType}
            onChange={handleTravelTypeChange}
          />
        </InlineFilter>
      </InlineFilterGroup>

      {children}
    </UndergroundMapModalHeader>
  );
}
